<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { splitBySentence, transformMarkedText } from '@/composables/splitSentence';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useAnalysisStore } from '@/store/modules/analysis';
import { getFiveClassForParts, splitYongHuiDesc } from './yongsinUsage.js';
import YongsinSpeechTail from '@/components/svg/YongsinSpeechTail.vue';
import YongHuiSummary from './YongHuiSummary.vue';
import YongHeeFiveChart from './YongHeeFiveChart.vue';
import YongHuiSummaryMobile from './YongHuiSummaryMobile.vue';

const props = defineProps({
  yData: Object,
  isMobile: Boolean,
});
const store = useAnalysisStore();
const sajuName = computed(() => store.userName);

const mainTit = computed(() => props.yData?.분석상세?.용희활용?.기본정보?.제목);
const mainDesc = computed(() => splitBySentence(props.yData?.분석상세?.용희활용?.기본정보?.설명));
const yongMainDesc = computed(() =>
  splitYongHuiDesc(props.yData?.분석상세?.용희활용?.기본정보?.용신),
);
const huiMainDesc = computed(() =>
  splitYongHuiDesc(props.yData?.분석상세?.용희활용?.기본정보?.희신),
);
const yongClass = computed(() => getFiveClassForParts(yongMainDesc.value));
const huiClass = computed(() => getFiveClassForParts(huiMainDesc.value));
const usefulAdvice = computed(() => props.yData?.분석상세?.용희활용?.기본정보?.실용조언);
const additionalDesc = computed(() => props.yData?.분석상세?.용희활용?.용희원소?.추가희신_설명);

// 차트 범례
// TODO: 컴포넌트분리 고려
const energyColors = {
  fire: '#c23535',
  earth: '#db9823',
  wood: '#5e9f1c',
  metal: '#949494',
  water: '#404040',
  inactive: '#ecf0f1',
};
const yongTimeChartData = computed(
  () => props.yData?.분석상세?.용희활용?.용희원소?.용신_원본?.지지,
);
const yongFiveChartData = computed(() => props.yData?.분석상세?.용희활용?.요약정보?.용신?.오행);
const heeTimeChartData = computed(() => props.yData?.분석상세?.용희활용?.용희원소?.희신_원본?.지지);
const heeFiveChartData = computed(() => props.yData?.분석상세?.용희활용?.요약정보?.희신?.오행);
const yKey = computed(() => elemKey(yongFiveChartData.value));
const hKey = computed(() => elemKey(heeFiveChartData.value));
const getEnergyTypeName = (type) => {
  const names = {
    fire: '화 (火)',
    earth: '토 (土)',
    wood: '목 (木)',
    metal: '금 (金)',
    water: '수 (水)',
  };
  return names[type] || '기타';
};
/** 지지 , 구분 */
const joinWithComma = (s) => [...String(s ?? '')].join(', ');
/* 오행 → 에너지키 */
const elemKey = (s) => {
  const m = String(s ?? '').match(/[목화토금수]/);
  const map = { 목: 'wood', 화: 'fire', 토: 'earth', 금: 'metal', 수: 'water' };
  return map[m?.[0]] ?? null;
};
const yLegend = computed(() => ({
  elem: yKey.value,
  color: energyColors[yKey.value] || '#ddd',
  text: joinWithComma(yongTimeChartData.value),
}));
const hLegend = computed(() => ({
  elem: hKey.value,
  color: energyColors[hKey.value] || '#ddd',
  text: joinWithComma(heeTimeChartData.value),
}));

const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...';
  }
  return text;
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">11</span>
          <span class="detail-tit__txt yongsin">용신과 희신 잘 활용하는 법</span>
        </div>
        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="yongsin-desc-wrapper gray">
            <span class="yongsin-usage__main-tit">
              {{ truncateText(sajuName, 8) }}님에게 균형을 맞춰주는<br />
              용신과 희신
              <span
                >활용 가이드
                <div class="yongsin-tit__underline" />
              </span>
            </span>
            <span class="yongsin-usage__sub-tit">
              중요한 날의 색상, 기운이 약할 때의 보충법 등 용신을 활용해서 나에게 힘이 되어주는 생활
              속 균형을 살펴볼 수 있어요.
            </span>
          </div>
          <div class="yongsin-usage__main-desc">
            <div class="useage-desc__top-wrapper">
              <div v-for="(str, i) in mainDesc" :key="i" class="usage-desc__top">
                {{ str }}
              </div>
            </div>

            <div class="usage-desc__body">
              <div class="usage-desc__body-top">
                <div class="usage-desc__bubble-wrapper">
                  <div class="usage-desc__speech-bubble" :class="yongClass">용신은?</div>
                  <YongsinSpeechTail class="usage-desc__speech-tail" :color="yongClass" />
                </div>
                <div class="usage-desc__five-txt" :class="`txt-${yongClass}`">
                  {{ yongMainDesc[1] }}
                </div>
              </div>
              <div class="usage-desc__body-bottom" :class="{ mobile: isMobile }">
                <div class="usage-bubble-wrapper" :class="{ mobile: isMobile }">
                  <div class="usage-desc__body-tooltip" :class="{ mobile: isMobile }">
                    {{ yongMainDesc[2] }}
                    <div class="usage-desc__tooltip-tail" :class="{ mobile: isMobile }" />
                  </div>
                </div>
              </div>
            </div>
            <div class="usage-desc__body">
              <div class="usage-desc__body-top">
                <div class="usage-desc__bubble-wrapper">
                  <div class="usage-desc__speech-bubble" :class="huiClass">희신은?</div>
                  <YongsinSpeechTail class="usage-desc__speech-tail" :color="huiClass" />
                </div>
                <div class="usage-desc__five-txt" :class="`txt-${huiClass}`">
                  {{ huiMainDesc[1] }}
                </div>
              </div>
              <div class="usage-desc__body-bottom" :class="{ mobile: isMobile }">
                <div class="usage-bubble-wrapper" :class="{ mobile: isMobile }">
                  <div class="usage-desc__body-tooltip" :class="{ mobile: isMobile }">
                    {{ huiMainDesc[2] }}
                    <div class="usage-desc__tooltip-tail" :class="{ mobile: isMobile }" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper content_info_area_box">
          <div class="yongsin-cont-box">
            <div class="yongsin-cont__tit-section">
              <span class="main-tit"> 📋 용신/희신 활용 요약 Tip </span>
            </div>
            <div class="yongsin-cont__body gray-box" :class="{ mobile: isMobile }">
              <YongHuiSummaryMobile v-if="isMobile" :yData="yData" />
              <YongHuiSummary v-else :yData="yData" />
            </div>
            <!-- <div class="yong-usage__text p-14">
              {{ usefulAdvice }}
            </div> -->
            <div class="yong-usage__text p-14" v-html="transformMarkedText(usefulAdvice)" />
          </div>
          <div class="yongsin-cont-box">
            <div class="yongsin-cont__tit-section">
              <span class="main-tit"> 📊 내 사주 오행에 담긴 용신과 희신 </span>
              <span class="sub-tit">
                내 사주 속 용신과 희신이 어떤 오행에 담겨 있는지 한눈에 볼 수 있어요.<br />
              </span>
              <span
                v-html="transformMarkedText(additionalDesc, { keywordClass: 'y-keyword' })"
                class="sub-tit"
              />
            </div>
            <div class="yongsin-cont__body gray-box yonghee" :class="{ mobile: isMobile }">
              <div class="yh-five-notice">
                {{ isMobile ? '👆 차트를 클릭해보세요!' : '🖱️ 그래프에 마우스를 올려보세요!' }}
              </div>
              <div class="yonghee-chart_wrapper" :class="{ mobile: isMobile }">
                <YongHeeFiveChart :yData="yData" :isMobile="isMobile" />
              </div>

              <div class="energy-chart__legend-items five-chart" :class="{ mobile: isMobile }">
                <div class="energy-chart__legend-item">
                  <div class="energy-chart__legend-top">
                    <span>용신: </span>
                    <div
                      class="energy-chart__legend-color"
                      :style="{ background: yLegend.color }"
                    ></div>
                    <div class="energy-chart__legend-wrapper">
                      <span>{{ getEnergyTypeName(yKey) }}</span>
                    </div>
                  </div>
                  <!-- <div class="energy-chart__legend-bottom">
                  <span>({{ yLegend.text }})</span>
                </div> -->
                </div>
                <div class="energy-chart__legend-item">
                  <div class="energy-chart__legend-top">
                    <span>희신: </span>
                    <div
                      class="energy-chart__legend-color"
                      :style="{ background: hLegend.color }"
                    ></div>
                    <div class="energy-chart__legend-wrapper">
                      <span>{{ getEnergyTypeName(hKey) }}</span>
                    </div>
                  </div>
                  <!-- <div class="energy-chart__legend-bottom">
                  <span>({{ hLegend.text }})</span>
                </div> -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
</style>
